/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// sweetalert2 components

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import EditUser from "Modals/EditUser";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// ProductsList page components
import ActionCell from "views/users/users-list/components/ActionCell";

import { getData, delData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Inactive" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Active" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

/**
 *
 * @returns
 */
function UsersList() {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform } = controller;

  const [showCreateModal, setCreateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [updateRecords, setUpdateRecords] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [templateCreatedId, setTemplateCreatedId] = useState(0);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "id", accessor: "id", width: "100px" },
      { Header: "firstname", accessor: "firstname", width: "100px" },
      { Header: "lastname", accessor: "lastname", width: "100px" },
      { Header: "username", accessor: "username", width: "100px" },
      { Header: "email", accessor: "email", width: "100px" },
      // { Header: "providers", accessor: "providers", Cell: makerollover, width: "120px" },
      // { Header: "types", accessor: "types", Cell: makeList, width: "120px" },
      // { Header: "sports", accessor: "sports", Cell: makerollover, width: "120px" },
      { Header: "roles", accessor: "roles", Cell: makeList, width: "120px" },
      { Header: "platforms", accessor: "platform_names", Cell: makerollover, width: "120px" },
      { Header: "permissions", accessor: "permissions", width: "120px" },
      { Header: "status", accessor: "status", width: "120px", Cell: makeStatusCell },
      { Header: "action", accessor: "action", width: "120px" },
    ],

    rows: [],
  });

  let userId = 1;

  function makerollover({ value, ...rest }) {
    let listItems = value.split(",");
    if (
      listItems.length > 0 &&
      (listItems[0] == null || listItems[0] == "null" || listItems[0] == "")
    ) {
      listItems = [];
    }

    return listItems.length > 0 ? (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {/* <div style={{ display: "block" }}> */}
              {listItems.map((item, index) => {
                return item ? (
                  <div key={index} style={{ width: "150px", display: "inline-flex" }}>
                    {item}
                  </div>
                ) : (
                  false
                );
              })}
              {/* </div> */}
            </div>
          </React.Fragment>
        }
      >
        <Button>
          {listItems.length}
          <span style={{ paddingLeft: "5px" }}> </span>

          <Icon>info_outlined</Icon>
        </Button>
      </HtmlTooltip>
    ) : (
      <div style={{ width: "60px", textAlign: "center" }}>{"-"}</div>
    );
  }

  function makeList({ value, ...rest }) {
    if (!value) return <></>;
    let listItems = value;

    return (
      <>
        {listItems.map((item) => {
          return (
            <SoftBadge
              style={{ marginRight: "3px" }}
              key={item}
              variant="gradient"
              color={item === "inplay" ? "info" : "secondary"}
              size="xs"
              badgeContent={item}
              container
            />
          );
        })}
      </>
    );
  }

  
  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeFiltersCell({ value, ...rest }) {
    return value === "" ? "None Filter Added" : value;
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeStatusCell({ value, ...rest }) {
    return value === "active" ? enabledTemplate : disabledTemplate;
  }

  function wrapWordCell({ value, ...rest }) {
    let headerName = rest.column.id;
    return <div className={headerName + "-cell"}>{value}</div>;
  }

  /**
   *
   */
  const handleCreateRecord = () => {
    setCreateModal(true);
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleOpenModal = (e, id) => {
    setRecordToDelete(id);
    setDeleteModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseModal = async (update, name) => {
    if (update) {
      //console.log("Creation completed, please Update!");
      initData();
    } else {
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
  };
  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      //console.log("Delete confirmed, please Update!");
      let setTemplatesEndpoint = `/api/v1/users/${id}`;
      let response = await delData(setTemplatesEndpoint, {
        Authorization: getAuthorizationHeader(),
      });
      if (response) {
        deleteRecord(id);
        initData();
      }
    } else {
      //console.log("Delete canceled, do nothing!");
    }
    setDeleteModal(false);
  };

  /**
   *
   */
  async function initData() {
    const userData = getUserData();
    let userId = userData.id;
    let roleName = userData.roles[0].name;
    let getTemplatesEndpoint = `/api/v1/users/${userId}/subusers`;
    setLoading(true);
    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      response.data.map((el) => {
        el.firstname = el.first_name;
        el.lastname = el.last_name;
        (el.status = el.status ? "active" : "inactive"),
          (el.action = (
            <ActionCell
              id={el.id}
              handleDeleteAction={(e) => handleOpenModal(e, el.id)}
              parent={el.parent_id.toString()}
              disabled={userData.roles[0].id * 1 >= el.role_id * 1}
            />
          ));
        el.platform_names = el.platform_names.join(", ");
        el.permissions = el.permission_ids.filter((p) => p != null).length;
      });
    } else {
    }

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    data.rows.sort((a, b) => b.id - a.id);

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);
  }

  useEffect(() => {
    if (!platform.label || dataTableData.rows.length == 0) initData();
  }, [platform.label]);

  useEffect(() => {
    return () => {
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* <CreateUser showModal={showCreateModal} signalCloseModal={handleCloseModal}></CreateUser> */}
      <EditUser
        showModal={showCreateModal}
        signalCloseModal={handleCloseModal}
        id={userId}
      ></EditUser>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"users"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Users
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the saved Users you have
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton variant="gradient" color="info" size="small" onClick={handleCreateRecord}>
                + new User
              </SoftButton>
            </Stack>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            searchValue={platform.label}
            canSearch
            openBackDrop={loading}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default UsersList;
